import React, { MouseEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import {
	ListItemText,
	TableHead,
	TableRow,
	TableCell,
	Grid,
	IconButton,
	Tooltip,
	LinearProgress,
	Typography,
	Box,
	Button,
} from '@mui/material';

import LocalGroceryStoreIcon from '@mui/icons-material/LocalGroceryStore';
import QRIcon from '@mui/icons-material/QrCode';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import SearchToolbar from 'components/SearchToolbar';
import ResponsiveTable from 'components/ResponsiveTable';
import ConfirmationDialog from 'components/ConfirmationDialog';
import DateFilters from 'components/SoldProductsFilters/DateFilters';
import OtherFilters from 'components/SoldProductsFilters/OtherFilters';

import { price } from 'lib/numbers';

import useInAppSales from 'hooks/useInAppSales';
import useInAppQrSales from 'hooks/useInAppQrSales';
import useInAppSalesActions from 'hooks/useInAppSalesActions';
import { useSoldProductsFilters } from 'hooks/useSoldProductsFilters';

import StatusPopover from './StatusPopover';

import { InAppPopulatedModes, InAppSaleType, OrderStatus } from 'lib/models/order';

import QrScanModal from 'components/QrScanModal';
import { fetchUserByDocument } from 'lib/models/users';
import { enqueueSnackbarError } from 'lib/helpers';
import { defaultSoldProductsRowsPerPage, soldProductsRowsPerPageOptions } from 'config/constants';

export default function ListSoldInAppProductsTable() {
	const { t } = useTranslation();

	const [loadingDocument, setLoadingDocument] = useState(false);
	const [scanQR, setScanQR] = useState(false);
	const [showQRResults, setShowQRResults] = useState(false);
	const [userDocument, setUserDocument] = useState('');
	const [userData, setUserData] = useState('');
	const [username, setUsername] = useState('');
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [selectedSale, setSelectedSale] = useState<Order | null>(null);

	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(defaultSoldProductsRowsPerPage);

	const { filters, dispatch } = useSoldProductsFilters({
		selectedStatuses: [OrderStatus.active],
		inAppSaleType: InAppSaleType.order,
	});
	const {
		from,
		to,
		confirmationFrom,
		confirmationTo,
		cancellationFrom,
		cancellationTo,
		reservationFrom,
		reservationTo,
		selectedStatuses,
		selectedVendors,
		selectedSections,
		inAppSaleType,
		reservationType,
	} = filters;

	const { refundSale, confirmSale, loading: loadingAction } = useInAppSalesActions();
	const [showConfirmation, setShowConfirmation] = useState('');
	const [showRefund, setShowRefund] = useState('');

	const {
		loading: loadingInAppQrSales,
		refetch: refetchQr,
		resetUsername,
		data: { total: qrTotal, elements: qrElements, user: qrUser, mode: qrMode },
	} = useInAppQrSales();

	const {
		loading: loadingInAppSales,
		data: { total: inAppTotal, elements: inAppElements, user: inAppUser },
		refetch,
	} = useInAppSales();

	const mode = showQRResults ? qrMode || InAppPopulatedModes.search : InAppPopulatedModes.search;
	const total = showQRResults ? qrTotal : inAppTotal;
	const elements = showQRResults ? qrElements : inAppElements;
	const elementsUser = showQRResults ? qrUser : inAppUser;
	const loading = loadingDocument || loadingAction || showQRResults ? loadingInAppQrSales : loadingInAppSales;

	const handleChangePage = (_: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const refresh = useCallback(() => {
		const searchParams = {
			limit: rowsPerPage,
			offset: page * rowsPerPage,
			from: from?.toISOString(),
			to: to?.toISOString(),
			confirmationFrom: confirmationFrom?.toISOString(),
			confirmationTo: confirmationTo?.toISOString(),
			cancellationFrom: cancellationFrom?.toISOString(),
			cancellationTo: cancellationTo?.toISOString(),
			reservationFrom: reservationFrom?.toISOString(),
			reservationTo: reservationTo?.toISOString(),
			statuses: selectedStatuses.length > 0 ? selectedStatuses.join(',') : undefined,
			vendors: selectedVendors.length > 0 ? selectedVendors.join(',') : undefined,
			sections: selectedSections.length > 0 ? selectedSections.join(',') : undefined,
			inAppSaleType: inAppSaleType || undefined,
			reservationType: reservationType || undefined,
		};

		if (showQRResults) {
			refetchQr({
				data: userData,
				...searchParams,
			});
		} else {
			refetch({
				user: username || undefined,
				...searchParams,
			});
		}
	}, [
		from,
		to,
		confirmationFrom,
		confirmationTo,
		cancellationFrom,
		cancellationTo,
		reservationFrom,
		reservationTo,
		selectedStatuses,
		selectedVendors,
		selectedSections,
		inAppSaleType,
		reservationType,
		page,
		refetch,
		refetchQr,
		rowsPerPage,
		showQRResults,
		username,
		userData,
	]);

	const onScan = useCallback((data: string) => {
		setScanQR(false);
		setShowQRResults(true);
		setUserData(data);
	}, []);

	const onClose = useCallback(() => {
		setScanQR(false);
		setShowQRResults(false);
		resetUsername();
	}, [resetUsername]);

	const handleClick = (event: MouseEvent<HTMLElement>, sale: Order) => {
		setAnchorEl(event.currentTarget);
		setSelectedSale(sale);
	};

	const handleClose = () => {
		setAnchorEl(null);
		setSelectedSale(null);
	};

	const confirm = useCallback(
		async (confirmed: boolean) => {
			if (confirmed) {
				await confirmSale(showConfirmation);
				refresh();
			}
			setShowConfirmation('');
		},
		[confirmSale, refresh, showConfirmation]
	);

	const cancel = useCallback(
		async (code?: string) => {
			setShowRefund('');
			if (code) {
				await refundSale(showRefund, code);
				refresh();
			}
		},
		[refundSale, refresh, showRefund]
	);

	useEffect(() => {
		const search = async () => {
			setLoadingDocument(true);
			const data = await fetchUserByDocument(userDocument);
			if (data) {
				setUsername(data.username);
			} else {
				enqueueSnackbarError('Usuario no encontrado');
			}
			setLoadingDocument(false);
		};

		if (userDocument) {
			search();
		} else {
			setUsername('');
			setUserDocument('');
		}
	}, [userDocument]);

	useEffect(() => {
		refresh();
	}, [refresh]);

	useEffect(() => {
		setPage(0);
	}, [
		from,
		to,
		confirmationFrom,
		confirmationTo,
		cancellationFrom,
		cancellationTo,
		reservationFrom,
		reservationTo,
		selectedStatuses,
		selectedVendors,
		selectedSections,
		inAppSaleType,
		reservationType,
	]);

	const open = Boolean(anchorEl);

	const userName = getUserName(elements[0]?.orderTo ?? elementsUser);

	return (
		<div style={{ position: 'relative' }}>
			{!!loading && <LinearProgress />}
			{showConfirmation && (
				<ConfirmationDialog
					onClose={confirm}
					title="Confirmación"
					description="¿Estás seguro que deseas confirmar la compra? Esta acción es irreversible."
					warningMessage="Está acción enviará una notificación al usuario y guardará la fecha y el usuario que realizó la confirmación"
				/>
			)}
			{showRefund && (
				<QrScanModal
					onClose={cancel}
					title="Devolución"
					description={
						<p>
							Para realizar una devolución es necesario la autorización de un administrador,{' '}
							<strong>para eso se necesita escanear un QR de autorización.</strong>
						</p>
					}
					warningMessage={
						<p>
							Está acción es <strong>irreversible</strong>, va a realizar una devolución, enviará una notificación al
							usuario y guardará la fecha y el <strong>usuario que realizó la devolución.</strong>
						</p>
					}
				/>
			)}

			{!showQRResults && (
				<Grid container spacing={2} columns={10} alignItems="center">
					<Grid item xs={8}>
						<SearchToolbar
							onSearch={(value) => setUserDocument(value)}
							live={false}
							placeholder="Documento"
							disabled={showQRResults}
							save={{ iconPosition: 'right' }}
						/>
					</Grid>
					<Grid item xs={8}>
						<DateFilters filters={filters} dispatch={dispatch} />
					</Grid>
					<Grid item xs={8}>
						<OtherFilters filters={filters} dispatch={dispatch} />
					</Grid>

					<Grid item xs={8} sm={2} md={2}>
						<Tooltip title={'Recargar'}>
							<IconButton onClick={refresh}>
								<RefreshIcon />
							</IconButton>
						</Tooltip>

						<Tooltip title={'Productos por usuario'}>
							<IconButton onClick={() => setScanQR(!scanQR)}>
								<QRIcon />
							</IconButton>
						</Tooltip>
					</Grid>
				</Grid>
			)}

			{showQRResults && mode !== InAppPopulatedModes.order && (
				<Grid container spacing={2} columns={10} alignItems="center" sx={{ mb: 2 }}>
					<Grid item xs={8}>
						<DateFilters filters={filters} dispatch={dispatch} />
					</Grid>
					<Grid item xs={8}>
						<OtherFilters filters={filters} dispatch={dispatch} />
					</Grid>
				</Grid>
			)}

			{showQRResults && (
				<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1">
						Mostrando resultados de <strong>{userName}</strong>
						{userName === 'Usuario no encontrado' && (
							<strong style={{ color: 'red' }}>
								{' (el código escaneado es inválido o venció, por favor volver a escanear)'}
							</strong>
						)}
					</Typography>

					<div>
						<Tooltip title={'Cancelar'}>
							<IconButton onClick={onClose}>
								<CloseIcon />
							</IconButton>
						</Tooltip>
					</div>
				</div>
			)}

			{scanQR && (
				<QrScanModal
					onClose={(code) => (code ? onScan(code) : setScanQR(false))}
					title="Escanear QR"
					description={<p>Escanear código QR de una compra o una reserva</p>}
				/>
			)}

			<ResponsiveTable
				elements={elements}
				list={{
					primaryKey: '_id',
					avatar: <LocalGroceryStoreIcon />,
					renderListItemText: (sale) => {
						const user = getUserName(sale.orderTo);
						return (
							<ListItemText
								primary={`${sale?.products[0]?.title || '-'}`}
								secondary={
									<div>
										<p>
											<strong>Fecha de compra</strong>: {moment(sale.createdAt).format('DD/MM/YYYY HH:mm')}
										</p>
										<p>
											<strong>Precio</strong>: {price(sale.products[0]?.price || 0)}
										</p>
										<p>
											<strong>Cantidad</strong>: {1}
										</p>
										<p>
											<strong>Usuario</strong>: {user} ({getUserName(sale.orderTo)})
										</p>
										<p>
											<strong>Fecha de reserva</strong>:{' '}
											{sale.inAppSaleData?.reservationDate
												? moment(sale.inAppSaleData?.reservationDate).format('DD/MM/YYYY HH:mm')
												: '-'}
										</p>
										<p>
											<strong>Estado</strong>: {getStatus(sale.status)}
										</p>
										<Box
											onClick={(e) => handleClick(e, sale)}
											sx={{
												display: 'inline-flex',
												alignItems: 'center',
												gap: 0.5,
												color: 'primary.main',
												cursor: 'pointer',
												zIndex: 9999,
											}}
										>
											{getStatus(sale.status)}
											<HelpOutlineIcon fontSize="small" />
										</Box>
										<StatusPopover
											open={open}
											anchorEl={anchorEl}
											handleClose={handleClose}
											selectedSale={selectedSale}
										/>
										<p>
											<strong>Total: {price(sale.total)}</strong>
										</p>
										<div>
											<Button
												disabled={sale.status !== 'active' || loading}
												onClick={() => setShowConfirmation(sale._id)}
											>
												Confirmar
											</Button>
											{/*
												A cancelled sale has already been refunded. The refund key is the order id
												and never expires, so asking again replays the first refund and answers
												with the same cancelled order: the money moves only once, but the screen
												would be indistinguishable from a refund just made. Keep this disabled.
											*/}
											<Button
												disabled={sale.status === OrderStatus.cancelled || loading}
												color="error"
												onClick={() => setShowRefund(sale._id)}
											>
												Devolución
											</Button>
										</div>
									</div>
								}
							/>
						);
					},
				}}
				table={{
					renderHead: () => (
						<TableHead>
							<TableRow>
								<TableCell align="left">Fecha de compra</TableCell>
								<TableCell align="left">Fecha de confirmación</TableCell>
								<TableCell align="left">Fecha de cancelación</TableCell>
								{inAppSaleType === InAppSaleType.reservation && <TableCell align="left">Fecha de reserva</TableCell>}
								<TableCell align="left">Usuario</TableCell>
								<TableCell align="left">Email</TableCell>
								<TableCell align="left">Usuario interno</TableCell>
								<TableCell align="left">Código Producto</TableCell>
								<TableCell align="left">Producto</TableCell>
								<TableCell align="left">Estado</TableCell>
								<TableCell align="left">{t('common:quantity')}</TableCell>
								<TableCell align="left">{t('common:total')}</TableCell>
								<TableCell align="left">Acciones</TableCell>
							</TableRow>
						</TableHead>
					),
					renderRow: (sale) => (
						<TableRow tabIndex={-1} key={`${sale._id}`}>
							<TableCell>{moment(sale.createdAt).format('DD/MM/YYYY HH:mm')}</TableCell>
							<TableCell>
								{sale.confirmationDate ? moment(sale.confirmationDate).format('DD/MM/YYYY HH:mm') : '-'}
							</TableCell>
							<TableCell>
								{sale.cancellationDate ? moment(sale.cancellationDate).format('DD/MM/YYYY HH:mm') : '-'}
							</TableCell>
							{inAppSaleType === InAppSaleType.reservation && (
								<TableCell>
									{sale.inAppSaleData?.reservationDate
										? moment(sale.inAppSaleData?.reservationDate).format('DD/MM/YYYY HH:mm')
										: '-'}
								</TableCell>
							)}
							<TableCell>{getUserName(sale.orderTo)}</TableCell>
							<TableCell>{sale.orderTo.email}</TableCell>
							<TableCell>{sale.confirmedBy?.username || sale.cancelledBy?.username || '-'}</TableCell>
							<TableCell>{sale.products[0]?.code || '-'}</TableCell>
							<TableCell>{sale.products[0]?.title}</TableCell>
							<TableCell>
								<Box
									onClick={(e) => handleClick(e, sale)}
									sx={{
										display: 'inline-flex',
										alignItems: 'center',
										gap: 0.5,
										color: 'primary.main',
										cursor: 'pointer',
									}}
								>
									{getStatus(sale.status)}
									<HelpOutlineIcon fontSize="small" />
								</Box>
							</TableCell>

							<StatusPopover open={open} anchorEl={anchorEl} handleClose={handleClose} selectedSale={selectedSale} />

							<TableCell>{1}</TableCell>
							<TableCell>{price(sale.total)}</TableCell>
							<TableCell>
								<div>
									<Button disabled={sale.status !== 'active' || loading} onClick={() => setShowConfirmation(sale._id)}>
										Confirmar
									</Button>
									{/* Already refunded: see the card view above for why this stays disabled. */}
									<Button
										disabled={sale.status === OrderStatus.cancelled || loading}
										color="error"
										onClick={() => setShowRefund(sale._id)}
									>
										Devolución
									</Button>
								</div>
							</TableCell>
						</TableRow>
					),
					pagination: {
						count: total,
						page,
						rowsPerPage,
						rowsPerPageOptions: soldProductsRowsPerPageOptions,
						onPageChange: handleChangePage,
						onRowsPerPageChange: handleChangeRowsPerPage,
					},
				}}
			/>
		</div>
	);
}

function getUserName(user?: OrderUser | null): string {
	return !user
		? 'Usuario no encontrado'
		: user.first_name && user.last_name
		? `${user.first_name} ${user.last_name}`
		: `Usuario (${user.username})`;
}

function getStatus(status: OrderStatus): string {
	switch (status) {
		case OrderStatus.active:
			return 'Pendiente de uso';
		case OrderStatus.closed:
			return 'Confirmado';
		case OrderStatus.cancelled:
			return 'Devuelto';
		default:
			return status;
	}
}
